import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, ScanLine, Activity, ChefHat, Recycle, Sparkles, Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import AgentChat from "@/components/AgentChat";
import { aiResolveOrg } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1543362906-acfc16c67564?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

const FEATURES = [
  { icon: Camera, title: "Photo Food ID", body: "Snap any dish or ingredient and FoodPulse names it instantly — plus portion, freshness and likely allergens." },
  { icon: ScanLine, title: "Ingredient & Label Analysis", body: "Point it at a nutrition label or ingredient list and get a plain-language breakdown: additives, added sugar, sodium, ultra-processed flags." },
  { icon: Activity, title: "Nutrition Lookup", body: "Get approximate calories and macros for a food or a whole plate — clearly labeled as estimates, never guessed exact numbers." },
  { icon: ChefHat, title: "Recipe Ideas", body: "Tell it what's in your fridge and get recipes you can actually make tonight, tuned to what you already have." },
  { icon: Recycle, title: "Food-Waste Tips", body: "Smart storage and use-it-up ideas that stretch your groceries, cut waste and save money every week." },
];

const STEPS = [
  { n: "01", t: "Snap or ask", d: "Upload a photo of a dish, a label, or just type what you have." },
  { n: "02", t: "FoodPulse analyzes", d: "Vision AI identifies the food and reasons over ingredients & nutrition." },
  { n: "03", t: "Cook & save", d: "Get recipes, nutrition and waste-reduction tips in seconds." },
];

export default function FoodPulse() {
  const [pk, setPk] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    aiResolveOrg("foodpulse").then((c) => setPk(c.public_key)).catch(() => setErr(true));
  }, []);

  const scrollToDemo = () => document.getElementById("foodpulse-demo")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#062a1e] text-white overflow-x-hidden" data-testid="foodpulse-page">
      {/* Nav */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-[#16A34A] flex items-center justify-center"><Sparkles size={18} /></span>
          <span className="font-heading font-extrabold text-lg tracking-tight">FoodPulse<span className="text-[#4ade80]">.net</span></span>
        </div>
        <Link to="/" className="text-sm text-emerald-100/70 hover:text-white flex items-center gap-1.5 transition-colors"><ArrowLeft size={14} /> WebsiteTech.pro</Link>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-16 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-200 bg-white/5 border border-emerald-400/20 rounded-full px-3 py-1.5 mb-6">
            <Sparkles size={13} className="text-[#4ade80]" /> Powered by WebsiteTech AI Core
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
            Snap it. Know it.<br /><span className="text-[#4ade80]">Cook it. Waste less.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="text-base md:text-lg text-emerald-50/80 mt-6 max-w-xl">
            FoodPulse is your AI food companion. Take a photo of any dish or ingredient label and get instant identification, nutrition, recipe ideas and smart tips to stop throwing food (and money) away.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }} className="flex flex-wrap gap-3 mt-8">
            <button onClick={scrollToDemo} data-testid="foodpulse-try-btn" className="bg-[#16A34A] hover:bg-[#15903f] text-white font-semibold rounded-xl px-6 py-3.5 flex items-center gap-2 transition-colors">
              Try the live demo <ArrowRight size={17} />
            </button>
            <a href="#features" className="border border-white/20 hover:border-white/40 rounded-xl px-6 py-3.5 font-semibold transition-colors">See what it does</a>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }} className="relative">
          <div className="absolute -inset-6 bg-[#16A34A]/20 blur-3xl rounded-full" />
          <img src={HERO_IMG} alt="Fresh ingredients" className="relative rounded-3xl w-full h-[360px] lg:h-[440px] object-cover border border-white/10 shadow-2xl" />
        </motion.div>
      </section>

      {/* Features */}
      <section id="features" className="bg-[#04211761] border-y border-white/5 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-center">One assistant, five superpowers</h2>
          <p className="text-emerald-50/70 text-center mt-3 max-w-2xl mx-auto text-base">Every tool runs on real vision AI — no guesswork, clearly-labeled estimates, and safety-first guidance.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                data-testid="foodpulse-feature" className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 hover:border-[#16A34A]/50 hover:bg-white/[0.06] transition-colors">
                <span className="w-11 h-11 rounded-xl bg-[#16A34A]/15 text-[#4ade80] flex items-center justify-center mb-4"><f.icon size={20} /></span>
                <h3 className="font-heading font-bold text-lg">{f.title}</h3>
                <p className="text-emerald-50/70 text-sm mt-2 leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Live demo */}
      <section id="foodpulse-demo" className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#4ade80]">Live demo</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Try it now — upload a food photo</h2>
            <p className="text-emerald-50/75 mt-4 text-base max-w-md">Click the image button in the chat, drop in a photo of your lunch or a packaged label, and watch FoodPulse identify it and break it down. Or just ask a food question.</p>
            <div className="mt-6 space-y-3">
              {STEPS.map((s) => (
                <div key={s.n} className="flex gap-4 items-start">
                  <span className="font-heading font-extrabold text-[#16A34A] text-xl w-9 shrink-0">{s.n}</span>
                  <div>
                    <p className="font-semibold">{s.t}</p>
                    <p className="text-emerald-50/60 text-sm">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="h-[600px] max-h-[80vh] bg-white rounded-3xl overflow-hidden shadow-2xl border border-white/10">
            {err ? (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm p-6 text-center" data-testid="foodpulse-demo-error">FoodPulse assistant is warming up. Please refresh in a moment.</div>
            ) : !pk ? (
              <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="animate-spin" size={26} /></div>
            ) : (
              <AgentChat publicKey={pk} />
            )}
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 px-4 text-center text-emerald-50/50 text-sm">
        FoodPulse.net · A demo assistant built on the <Link to="/" className="text-[#4ade80] hover:underline">WebsiteTech AI Core</Link>
      </footer>
    </div>
  );
}
